/**
 * The QA home (docs/ux/qa.md): four tiles, four queues under tabs, one
 * dense table at a time, the filter bar and the status key. Nothing is
 * decided here; every row opens the screen where the work is. Below 768 px
 * the table gives way to cards and the filters to a sheet.
 */

import type { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { say } from '@/app/announcer';
import { useOnline } from '@/app/online';
import type { QueueRow } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { DaysCell } from '@/components/matter/DaysCell';
import { FlagMenu } from '@/components/matter/FlagMenu';
import { StatusChip } from '@/components/matter/StatusChip';
import { FilterBar } from '@/components/shell/FilterBar';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { MatterCard, MatterCardList } from '@/components/shell/MatterCard';
import { PageHeader } from '@/components/shell/PageHeader';
import { QueueTable } from '@/components/shell/QueueTable';
import { QueueTabs } from '@/components/shell/QueueTabs';
import { Tile, TileRow } from '@/components/shell/Tile';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { useShellWidth } from '@/components/shell/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DueCell } from '@/features/pipeline/columns';
import { useDoctorCounts } from '@/features/dr/useDoctorQueue';
import { cardPairs, columnsFor } from './columns';
import { EMPTY_COPY, TILE_COPY, openLabel, subStateFor } from './describe';
import { QUEUE_IDS, QUEUE_LABEL, RISKS, TAB_IDS, activeQaFilters, allRows, applyQueueFilters, queueTarget, rowId, rowQueue, useQaFilters, useQaQueues, type QaFilters, type QueueTab, type Risk, type TabRow } from './useQaQueues';

const TILE_TONE: Record<Risk, 'neutral' | 'hot' | 'warning'> = { awaiting_qa: 'neutral', with_doctor: 'neutral', overdue: 'hot', due48: 'warning', past_mark: 'hot' };
const PHONE_TILES: readonly Risk[] = ['overdue', 'awaiting_qa'];

function uniqueDoctors(rows: QueueRow[]): Array<{ slug: string; name: string }> {
  const seen = new Map<string, string>();
  for (const r of rows) if (!seen.has(r.doctor.slug)) seen.set(r.doctor.slug, r.doctor.name);
  return [...seen.entries()].map(([slug, name]) => ({ slug, name })).sort((a, b) => a.name.localeCompare(b.name));
}

export function QaPage() {
  useShellWidth('wide');
  const { filters, update, clear } = useQaFilters();
  const queues = useQaQueues();
  const phone = useIsPhone();
  const online = useOnline();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<QaFilters>(filters);

  const queue = filters.queue;
  const all: TabRow[] = useMemo(() => (queue === 'all' ? allRows(queues.data) : queues.data?.[queue] ?? []), [queue, queues.data]);
  const visible = useMemo(() => applyQueueFilters(all, filters), [all, filters]);
  const doctors = useMemo(() => uniqueDoctors(all), [all]);
  // The URL's doctor may not be in this queue (the filter outlives a queue switch): named from any queue, kept in the select, so the empty result explains itself.
  const absentSlug = filters.doctor && !doctors.some((d) => d.slug === filters.doctor) ? filters.doctor : null;
  const registry = useDoctorCounts(absentSlug !== null);
  const absentDoctor = useMemo(() => {
    if (!absentSlug) return null;
    const everywhere = QUEUE_IDS.flatMap((id) => queues.data?.[id] ?? []);
    const name = everywhere.find((r) => r.doctor.slug === absentSlug)?.doctor.name ?? registry.data?.find((d) => d.slug === absentSlug)?.name ?? absentSlug;
    return { slug: absentSlug, name };
  }, [absentSlug, queues.data, registry.data]);
  const columns = useMemo(() => columnsFor(queue), [queue]);
  const sorting: SortingState = filters.sort ? [{ id: filters.sort, desc: filters.dir === 'desc' }] : [];
  const tiles = queues.data?.tiles;
  const counts = queues.data?.counts;

  // The All tab has no count of its own: it is the sum of the queues, and the announcement reads the same number as the tab.
  const tabCount = (id: QueueTab): number | null => (counts ? (id === 'all' ? QUEUE_IDS.reduce((n, q) => n + counts[q], 0) : counts[id]) : null);
  const tabs = TAB_IDS.map((id) => ({ value: id, label: QUEUE_LABEL[id], ...(counts ? { count: tabCount(id) ?? 0 } : {}) }));
  const tileValue = (risk: Risk): number | null => (tiles ? { awaiting_qa: tiles.awaitingQa, with_doctor: tiles.withDoctor, overdue: tiles.overdue, due48: tiles.dueWithin48h, past_mark: tiles.pastReportMark }[risk] : null);
  const pressRisk = (risk: Risk) => update({ risk: filters.risk === risk ? null : risk });
  const results = `Showing ${visible.length} of ${all.length} in ${QUEUE_LABEL[queue]}${online ? '' : '. Showing the last loaded queues.'}`;

  function openSheet(open: boolean) {
    if (open) setDraft(filters);
    setSheetOpen(open);
  }

  const header = <PageHeader title="QA" description="Everything waiting on QA, in due-date order." />;

  if (queues.isError) {
    return (
      <div className="flex flex-col gap-3">
        {header}
        <QueueTabs tabs={tabs} value={queue} onChange={(v) => update({ queue: v as QaFilters['queue'] })} label="QA queues" panelId="qa-queue-panel" />
        <ErrorPanel message="The QA queues could not be loaded." onRetry={() => void queues.refetch()} offline={!online} />
      </div>
    );
  }

  const empty = all.length === 0 ? (
    <EmptyState title={EMPTY_COPY[queue].title} description={EMPTY_COPY[queue].description} />
  ) : absentDoctor && !filters.q && !filters.risk ? (
    <EmptyState title={`No cases for ${absentDoctor.name} in ${QUEUE_LABEL[queue]}.`} description={`The doctor filter stays across queues. Clear it to see all ${all.length}.`} action={<Button variant="link" className="h-auto p-0" onClick={clear}>Clear filters</Button>} />
  ) : (
    <EmptyState title={`No matches in ${QUEUE_LABEL[queue]}.`} description={`Clear the filters to see all ${all.length}.`} action={<Button variant="link" className="h-auto p-0" onClick={clear}>Clear filters</Button>} />
  );

  return (
    <div className="flex flex-col gap-3">
      {header}
      <TileRow label="Today's load">
        {RISKS.filter((r) => !phone || PHONE_TILES.includes(r)).map((r) => (
          <Tile key={r} value={tileValue(r)} label={TILE_COPY[r].label} tone={TILE_TONE[r]} pressed={filters.risk === r} onPress={() => pressRisk(r)} description={TILE_COPY[r].description} loading={queues.isPending} />
        ))}
      </TileRow>
      <QueueTabs tabs={tabs} value={queue} onChange={(v) => { const next = v as QaFilters['queue']; say(`${QUEUE_LABEL[next]}, ${tabCount(next) ?? ''} cases`); update({ queue: next, sort: null }); }} label="QA queues" panelId="qa-queue-panel" />
      <FilterBar
        activeCount={activeQaFilters(filters)}
        // The phone's hint is the results line alone: the filter bar's hint cannot shrink beside the Filters button.
        hint={phone ? <span role="status" data-slot="results-line">{results}</span> : <span className="flex flex-wrap items-center gap-x-3"><span>{filters.sort ? <>Order: {filters.sort}, {filters.dir === 'desc' ? 'descending' : 'ascending'}. <Button variant="link" className="h-auto p-0 text-xs" onClick={() => update({ sort: null })}>Reset</Button></> : 'Order: due date, then Urgent'}</span><span role="status" data-slot="results-line">{results}</span></span>}
        sheetOpen={sheetOpen}
        onSheetOpenChange={openSheet}
        onApply={() => { update({ ...draft }); setSheetOpen(false); }}
        onClear={() => { clear(); setSheetOpen(false); }}
        desktop={
          <>
            <Input type="search" value={filters.q} onChange={(e) => update({ q: e.target.value })} placeholder="Search this queue" aria-label="Search this queue" className="h-9 w-60 bg-beam-paper" />
            <Select value={filters.doctor ?? 'all'} onValueChange={(v) => update({ doctor: v === 'all' ? null : v })}>
              <SelectTrigger aria-label="Doctor" className="h-9 min-w-40 bg-beam-paper"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Doctor: all</SelectItem>
                {doctors.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
                {absentDoctor ? <SelectItem value={absentDoctor.slug}>{absentDoctor.name} (not in this queue)</SelectItem> : null}
              </SelectContent>
            </Select>
          </>
        }
        sheet={
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor="qa-sheet-q">Search this queue</Label>
              <Input id="qa-sheet-q" type="search" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} className="h-11 text-base" />
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium">Doctor</legend>
              <RadioGroup value={draft.doctor ?? 'all'} onValueChange={(v) => setDraft({ ...draft, doctor: v === 'all' ? null : v })}>
                <div className="flex min-h-11 items-center gap-3"><RadioGroupItem value="all" id="qa-doc-all" /><Label htmlFor="qa-doc-all">All doctors</Label></div>
                {doctors.map((d) => <div key={d.slug} className="flex min-h-11 items-center gap-3"><RadioGroupItem value={d.slug} id={`qa-doc-${d.slug}`} /><Label htmlFor={`qa-doc-${d.slug}`}>{d.name}</Label></div>)}
                {absentDoctor ? <div className="flex min-h-11 items-center gap-3"><RadioGroupItem value={absentDoctor.slug} id={`qa-doc-${absentDoctor.slug}`} /><Label htmlFor={`qa-doc-${absentDoctor.slug}`}>{absentDoctor.name} (not in this queue)</Label></div> : null}
              </RadioGroup>
            </fieldset>
            <div className="flex min-h-11 items-center justify-between gap-3"><Label htmlFor="qa-sheet-overdue">Overdue only</Label><Switch id="qa-sheet-overdue" checked={draft.risk === 'overdue'} onCheckedChange={(on) => setDraft({ ...draft, risk: on ? 'overdue' : null })} /></div>
            <div className="flex min-h-11 items-center justify-between gap-3"><Label htmlFor="qa-sheet-due48">Due within 48 hours only</Label><Switch id="qa-sheet-due48" checked={draft.risk === 'due48'} onCheckedChange={(on) => setDraft({ ...draft, risk: on ? 'due48' : null })} /></div>
          </div>
        }
      />
      <div id="qa-queue-panel" role="tabpanel" aria-label={`${QUEUE_LABEL[queue]} queue`}>
      {phone ? (
        <MatterCardList state={queues.isPending ? 'loading' : 'ready'}>
          {!queues.isPending && visible.length === 0 ? empty : visible.map((row) => (
            <MatterCard
              // Keyed by the case, not by position: the queue reorders when a row is
              // marked urgent, and an index key rewrites every card in place, so the
              // flag menu open on one row would find itself attached to another.
              key={row.ref}
              href={queueTarget(rowQueue(queue, row), row)}
              reference={row.ref}
              patient={row.patientAsOnLoi}
              chip={<StatusChip status={row.status} size="sm" subState={subStateFor(rowQueue(queue, row), row) ?? undefined} />}
              due={<DueCell dueDate={row.dueDate} />}
              days={<DaysCell days={row.daysWaiting} sla={row.slaState} allowedDays={row.allowedDays} status={row.status} />}
              pairs={cardPairs(rowQueue(queue, row), row)}
              flags={<FlagMenu matterRef={row.ref} flags={row.flags} max={9} layout="inline" />}
              overdue={row.slaState === 'overdue'}
              action={openLabel(rowQueue(queue, row), row).text}
            />
          ))}
        </MatterCardList>
      ) : (
        <QueueTable
          columns={columns}
          data={visible}
          sorting={sorting}
          onSortingChange={(updater) => { const next = typeof updater === 'function' ? updater(sorting) : updater; const first = next[0]; update(first ? { sort: first.id, dir: first.desc ? 'desc' : 'asc' } : { sort: null }); }}
          getRowId={(r) => rowId(r as TabRow)}
          rowHref={(r) => queueTarget(rowQueue(queue, r as TabRow), r)}
          rowState={(r) => (r.slaState === 'overdue' ? 'overdue' : 'default')}
          caption={`${QUEUE_LABEL[queue]} queue: ${visible.length} ${visible.length === 1 ? 'case' : 'cases'}, due-date order`}
          state={queues.isPending ? 'loading' : 'ready'}
          empty={empty}
        />
      )}
      </div>
      <Legend>
        <LegendItem term={<StatusChip status={7} size="sm" />}>the doctor has submitted; QA reviews the draft and proposes edits, asks or completes.</LegendItem>
        <LegendItem term={<span className="font-medium">Resubmission</span>}>a 7 with a round behind it: the doctor has decided a round and the draft is back with QA.</LegendItem>
        <LegendItem term={<StatusChip status={8} size="sm" />}>QA has sent suggestions; the doctor is deciding. Read only for QA.</LegendItem>
        <LegendItem term={<span className="font-medium">Open work</span>}>suggestions the doctor has not decided, questions from QA the doctor has not answered, answer boxes still empty.</LegendItem>
        <LegendItem term={<span className="font-medium">Sort review</span>}>Review required, In review or Escalated until QA completes the review; To decide counts the flagged items still open.</LegendItem>
        <LegendItem term={<span className="font-medium">LOI review</span>}>from the letter's arrival until QA approves the form; Blocking counts what stops approval.</LegendItem>
        <LegendItem term={<span className="font-medium">SLA and Urgent</span>}>the referrer allowance at risk, computed from sla.json; the priority flag set by admin.</LegendItem>
      </Legend>
    </div>
  );
}
